import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";

export const SOLUTIONS = [
    {
        slug: "everyday-life",
        title: "Everyday Life Concierge®",
        eyebrow: "Pillar 01",
        image: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/editorial/04-courier-and-keys-duotone.jpg",
        imageAlt: "An overhead still-life of a courier package, house keys, a handwritten Tuesday list and a half-finished coffee on a worn wooden desk.",
        tagline: "For the personal tasks that quietly eat into an employee's day.",
        body: "Restaurant bookings, errands, gifting, courier support, travel planning, home repairs, bill payments, appointment coordination, purchases, research, local assistance and last-minute needs. Anything an employee would otherwise do during office hours, lunch breaks, weekends or in a moment of stress — we help manage with care and discretion.",
        bullets: [
            "Restaurant reservations, errands, gifting",
            "Bill payments, document running, courier support",
            "Home repairs and trusted vendor coordination",
            "Travel planning and last-mile logistics",
            "Local assistance and last-minute needs",
        ],
        personas: [
            { title: "The Young Joinee in a New City", body: "Needs help finding a reliable laundry service, fixing a phone screen, sending documents home, booking a weekend plan, or arranging something urgent without knowing the city well." },
            { title: "The Mid-Level Manager With No Time", body: "Needs errands, repairs, purchases, restaurant reservations, gifting, courier support and everyday personal tasks handled between back-to-back meetings." },
            { title: "The Senior Leader Protecting Time", body: "Needs discreet, dependable support for daily life coordination, premium reservations, home assistance, travel-related tasks and last-minute personal requirements." },
        ],
        means: "employees stop losing work hours to life admin.",
    },
    {
        slug: "family-care",
        title: "Family & Care Support",
        eyebrow: "Pillar 02",
        image: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/editorial/01-parent-on-call-duotone.jpg",
        imageAlt: "A working parent at a wooden home desk, phone tucked at the ear, writing into an open journal beside a child's hand-drawn note and a navy passport.",
        tagline: "Because employees are also parents, children, partners and caregivers.",
        body: "We assist with senior parent support, child-related coordination, doctor appointments, school-related needs, home assistance, family travel, emergency errands, celebration planning and everyday household responsibilities. This pillar helps HR support the real lives employees carry behind their work responsibilities.",
        bullets: [
            "Senior parent support and elder care coordination",
            "Child-related coordination and school needs",
            "Family travel and household assistance",
            "Doctor and home-visit coordination",
            "Emergency errands when life moves fast",
        ],
        personas: [
            { title: "The Working Parent", body: "Needs help with school coordination, children's activities, birthday planning, tutor searches, family appointments, weekend plans and emergency errands." },
            { title: "The Caregiver Employee", body: "Needs support for ageing parents, doctor visits, medicine coordination, home assistance, diagnostics, transport and urgent family requirements." },
            { title: "The Senior Professional Managing a Household", body: "Needs reliable help coordinating family travel, household vendors, celebrations, home repairs and personal tasks that require trusted follow-through." },
        ],
        means: "employees do not have to choose between family responsibility and work focus.",
    },
    {
        slug: "travel-mobility",
        title: "Travel, Mobility & Relocation Concierge®",
        eyebrow: "Pillar 03",
        image: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/editorial/03-suitcase-and-shoes-duotone.jpg",
        imageAlt: "A half-packed leather suitcase open on a hardwood floor beside polished black office shoes, with a folded white shirt, a passport and a set of keys placed on top.",
        tagline: "For employees who are constantly moving, relocating or managing travel pressure.",
        body: "Flight and hotel assistance, visa and passport coordination, airport support, cab arrangements, itinerary planning, relocation assistance, city orientation, house search support, local vendor coordination and travel disruption assistance. Whether an employee is moving cities, travelling for work or planning a family break — we make the experience smoother.",
        bullets: [
            "Flight and hotel assistance, anywhere",
            "Visa and passport coordination",
            "Airport, cab and itinerary support",
            "Relocation, city orientation, house search",
            "Travel disruption response",
        ],
        personas: [
            { title: "The Employee Relocating for Work", body: "Needs help with city orientation, house search coordination, local services, domestic setup, school or neighbourhood research and settling-in support." },
            { title: "The Frequent Business Traveller", body: "Needs flights, hotels, airport support, itinerary coordination, visa assistance, cab arrangements and disruption management." },
            { title: "The Family Traveller", body: "Needs holidays planned around children, parents, comfort, dietary needs, airport assistance, hotel preferences and reliable on-ground coordination." },
        ],
        means: "travel becomes less stressful, whether it is for work, family or relocation.",
    },
    {
        slug: "health-wellness",
        title: "Health, Wellness & Emergency Assistance",
        eyebrow: "Pillar 04",
        image: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/editorial/05-bedside-medicine-duotone.jpg",
        imageAlt: "A quiet bedside still-life: a glass of water, two amber medicine bottles, a folded prescription and reading glasses on a small wooden side-table beside a softly lit bed.",
        tagline: "Practical help when employees or their families need support most.",
        body: "Doctor appointments, diagnostic bookings, pharmacy coordination, hospital information, wellness appointments, fitness and therapy bookings, urgent household support and emergency response coordination. We do not replace medical professionals. We help employees navigate the practical side of care when time, clarity and trusted follow-through matter.",
        bullets: [
            "Doctor and diagnostic bookings",
            "Pharmacy coordination and hospital information",
            "Wellness, fitness and therapy bookings",
            "Urgent household support",
            "Emergency response coordination",
        ],
        personas: [
            { title: "The Employee Managing a Health Scare", body: "Needs help booking a doctor, arranging diagnostics, coordinating pharmacy support, finding hospital information or managing urgent practical requirements." },
            { title: "The Employee Supporting Parents", body: "Needs appointments, transport, medicine coordination, test bookings, caregiver assistance and follow-ups for elderly family members." },
            { title: "The Burnt-Out Manager", body: "Needs wellness appointments, therapy or fitness bookings, preventive health check coordination and practical help to create space for recovery." },
        ],
        means: "wellbeing moves from advice to action.",
    },
    {
        slug: "documentation-government",
        title: "Documentation & Government Assistance",
        eyebrow: "Pillar 05",
        image: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/editorial/02-documents-on-desk-duotone.jpg",
        imageAlt: "Hands sorting paperwork on a wooden desk: a dark Indian passport, a folded form, a worn manila envelope and a notebook with handwritten notes.",
        tagline: "For the work that is often stressful, time-consuming and confusing.",
        body: "Passport, visa, PAN, Aadhaar, driving licence, certificates, municipal work, registrations, document pickup and submission coordination, form assistance, appointment scheduling and follow-ups with relevant offices or service providers. In India, this is one of the biggest invisible stress points for employees. We help reduce the friction.",
        bullets: [
            "Passport, visa, PAN, Aadhaar, driving licence",
            "Municipal work and registrations",
            "Document pickup and submission",
            "Form assistance and appointment scheduling",
            "Follow-ups with offices and service providers",
        ],
        personas: [
            { title: "The Young Professional", body: "Needs help with passport renewal, PAN, Aadhaar, driving licence, address updates, visa documentation or certificate coordination." },
            { title: "The Employee With Family Paperwork", body: "Needs support for children's documents, parent documentation, legal paperwork coordination, municipal requirements or appointment scheduling." },
            { title: "The Senior Leader With No Time for Follow-Up", body: "Needs forms reviewed, appointments booked, document pickups arranged, submissions coordinated and follow-ups managed discreetly." },
        ],
        means: "paperwork stops becoming a hidden tax on employee time.",
    },
    {
        slug: "moments-celebrations",
        title: "Moments, Celebrations & Experiences",
        eyebrow: "Pillar 06",
        image: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/editorial/06-gift-wrapping-duotone.jpg",
        imageAlt: "A pair of hands tying a pale silk ribbon around a small wrapped gift on a wooden table, beside a handwritten card and a single dried flower.",
        tagline: "Because employee wellbeing is not only about solving problems. It is also about helping people enjoy life.",
        body: "Birthdays, anniversaries, festivals, family dinners, weekend getaways, surprise gifts, children's celebrations, milestone planning, premium dining, tickets, experiences and special occasion support. We help employees create meaningful moments without adding another planning burden to their already full lives.",
        bullets: [
            "Birthdays, anniversaries, festivals",
            "Family dinners and weekend getaways",
            "Curated gifting and surprise planning",
            "Premium dining, tickets, experiences",
            "Milestone and special occasion support",
        ],
        personas: [
            { title: "The Employee Who Forgot an Occasion", body: "Needs a last-minute gift, flowers, cake, dinner booking, message, courier or surprise arranged beautifully and quickly." },
            { title: "The Working Parent Planning a Celebration", body: "Needs help with birthdays, family dinners, school celebrations, festive gifting, weekend experiences and children's activities." },
            { title: "The Senior Executive Hosting or Gifting", body: "Needs refined dining, premium gifting, private experiences, milestone planning, tickets, access and thoughtful personalisation." },
        ],
        means: "employees get to enjoy life's moments without carrying the entire planning burden.",
    },
];

export function SolutionsIndex() {
    return (
        <>
            <section className="pt-40 md:pt-48 pb-16" data-testid="solutions-hero">
                <div className="container-prose">
                    <p className="eyebrow">Solutions</p>
                    <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 max-w-4xl reveal">
                        Six Pillars. <span className="italic text-[hsl(var(--brass))]">One Promise.</span>
                    </h1>
                    <p className="mt-8 max-w-2xl text-[16px] md:text-[18px] leading-relaxed text-[hsl(var(--ink))] reveal" style={{ animationDelay: "0.15s" }}>
                        We help employees manage the invisible workload of life. Every request begins with Mira, who gathers and structures the need. But every outcome is handled by a trained Les Concierges® Concierge® — who confirms, sources, coordinates and closes the loop. <span className="italic">Human where it matters. Digital where it helps. Available wherever life happens.</span>
                    </p>
                </div>
            </section>

            <section className="section-tight" data-testid="solutions-grid">
                <div className="container-prose">
                    <div className="grid md:grid-cols-2 gap-y-16 gap-x-16">
                        {SOLUTIONS.map((s) => (
                            <Link
                                to={`/solutions/${s.slug}`}
                                key={s.slug}
                                className="tile group"
                                data-testid={`solutions-tile-${s.slug}`}
                            >
                                <p className="eyebrow">{s.eyebrow}</p>
                                <h3 className="font-serif text-3xl md:text-[36px] leading-tight mt-3">
                                    {s.title}
                                </h3>
                                <p className="mt-4 text-[15px] md:text-[16px] leading-relaxed text-[hsl(var(--ink))]">
                                    {s.tagline}
                                </p>
                                <p className="mt-5 eyebrow group-hover:text-[hsl(var(--brass))] transition-colors">
                                    Read more →
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}

export function SolutionDetail() {
    const { slug } = useParams();
    const s = SOLUTIONS.find((x) => x.slug === slug);
    if (!s) return <Navigate to="/solutions" replace />;
    return (
        <>
            <section className="pt-40 md:pt-48 pb-12" data-testid={`solution-detail-${s.slug}`}>
                <div className="container-prose grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-8">
                        <p className="eyebrow">{s.eyebrow}</p>
                        <h1 className="font-serif text-5xl md:text-[72px] leading-[1.04] mt-6 reveal">
                            {s.title}
                        </h1>
                        <p className="font-serif italic text-[hsl(var(--brass))] text-2xl md:text-[28px] mt-6">
                            {s.tagline}
                        </p>
                    </div>
                </div>
            </section>

            {s.image && (
                <section className="pb-12 md:pb-16" data-testid={`solution-image-${s.slug}`}>
                    <div className="container-prose">
                        <figure className="relative overflow-hidden">
                            <img
                                src={s.image}
                                alt={s.imageAlt || s.title}
                                loading="lazy"
                                className="w-full h-[42vh] md:h-[58vh] object-cover reveal-slow"
                                style={{ filter: "brightness(0.97)" }}
                            />
                            <div
                                className="pointer-events-none absolute inset-0"
                                style={{
                                    boxShadow:
                                        "inset 0 0 0 1px hsl(var(--brass) / 0.35)",
                                }}
                            />
                        </figure>
                    </div>
                </section>
            )}

            <section className="pb-16 md:pb-24">
                <div className="container-prose grid md:grid-cols-12 gap-12">
                    <p className="md:col-span-7 text-[17px] md:text-[19px] leading-relaxed text-[hsl(var(--ink))]">
                        {s.body}
                    </p>
                    <ul className="md:col-span-4 md:col-start-9 space-y-4">
                        {s.bullets.map((b, i) => (
                            <li key={i} className="flex gap-4 text-[15px] leading-relaxed text-[hsl(var(--ink))]">
                                <span className="font-serif italic text-[hsl(var(--brass))] mt-0.5">{`0${i + 1}`}</span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Who actually uses this pillar */}
            {s.personas && (
                <section
                    className="section"
                    style={{ background: "hsl(var(--paper))" }}
                    data-testid={`pillar-personas-${s.slug}`}
                >
                    <div className="container-prose">
                        <p className="eyebrow">Who actually uses this pillar?</p>
                        <h2 className="font-serif text-4xl md:text-5xl mt-5 leading-tight max-w-3xl">
                            A young employee. <br className="hidden md:block" />
                            A stretched manager. <br className="hidden md:block" />
                            <span className="italic text-[hsl(var(--brass))]">A senior leader.</span>
                        </h2>
                        <p className="mt-6 font-serif italic text-[18px] md:text-[20px] leading-snug text-[hsl(var(--muted))] max-w-2xl">
                            Different life stages. Same promise: life becomes easier to manage.
                        </p>

                        <div className="mt-14 grid md:grid-cols-3 gap-8">
                            {s.personas.map((p, i) => (
                                <div
                                    key={i}
                                    className="bg-[hsl(var(--cream))] p-7 md:p-9 border-t-2 border-[hsl(var(--brass))]"
                                    data-testid={`persona-card-${i}`}
                                >
                                    <p
                                        className="eyebrow"
                                        style={{ color: "hsl(var(--brass))" }}
                                    >
                                        {`0${i + 1}`}
                                    </p>
                                    <h3 className="font-serif text-[22px] md:text-[24px] leading-snug mt-3 text-[hsl(var(--navy))]">
                                        {p.title}
                                    </h3>
                                    <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-[hsl(var(--ink))]">
                                        {p.body}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {s.means && (
                            <p className="mt-14 max-w-3xl font-serif italic text-[20px] md:text-[24px] leading-snug text-[hsl(var(--navy))]" data-testid="pillar-means">
                                What this really means: {s.means}
                            </p>
                        )}
                    </div>
                </section>
            )}

            <section className="section" style={{ background: "hsl(var(--cream))" }}>
                <div className="container-prose max-w-3xl text-center">
                    <p className="font-serif italic text-[22px] md:text-[28px] leading-relaxed text-[hsl(var(--navy))]">
                        Different employees. Different pressures. One support system. <br className="hidden md:block" />
                        Mira understands the request. Les Concierges® gets it done.
                    </p>
                    <Link to="/connect" className="btn-primary mt-12" data-testid="solution-cta-connect">
                        Speak to our Enterprise Team
                    </Link>
                </div>
            </section>
        </>
    );
}
